import React, { useState, useRef, useEffect, useMemo } from "react";
import { BACKEND_HTTP_BASE_URL, BRANCH_SEARCH_BASE_URL, CHAT_API_URL, LEAD_CREATE_URL, SERVICE_REQUEST_URL, DUPLICATE_LEAD_MESSAGE, loanTypeOptions, serviceRequestTypes } from "./constants";
import { validateStepInput, validateLeadPayload } from "./utils/validation";
import HomePage from "./components/HomePage";
import MessageList from "./components/MessageList";
import InputArea from "./components/InputArea";
import OnboardingForm from "./components/OnboardingForm";
import ServiceRequestForm from "./components/ServiceRequestForm";
import FAQ from "./components/FAQ";
import CalculatorsPage from "./components/CalculatorsPage";
import ExistingCustomer from "./components/ExistingCustomer";


const App = () => {
  const [isChatActive, setIsChatActive] = useState(false);
  const [loanDetailsActive, setLoanDetailsActive] = useState(false);
  const [loanDetailsSubmitting, setLoanDetailsSubmitting] = useState(false);
  const [chatDisabled, setChatDisabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [onboardingActive, setOnboardingActive] = useState(false);
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);
  const [onboardingError, setOnboardingError] = useState(null);
  const [onboardingData, setOnboardingData] = useState({});
  const [onboardingStepIndex, setOnboardingStepIndex] = useState(0);
  const [isEmiCalculatorOpen, setIsEmiCalculatorOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [showCalculatorsPage, setShowCalculatorsPage] = useState(false);
  const [showExistingCustomer, setShowExistingCustomer] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [contactUsActive, setContactUsActive] = useState(false);
  const [branchSearchActive, setBranchSearchActive] = useState(false);
  const [existingCustomerActive, setExistingCustomerActive] = useState(false);
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(14.8);
  const [tenureYears, setTenureYears] = useState(10);
  const [serviceRequestActive, setServiceRequestActive] = useState(false);
  const [serviceRequestSubmitting, setServiceRequestSubmitting] = useState(false);
  const [serviceRequestError, setServiceRequestError] = useState(null);
  const [serviceRequestData, setServiceRequestData] = useState({});
  const [serviceRequestStepIndex, setServiceRequestStepIndex] = useState(0);
  
  const messagesEndRef = useRef(null);
  const lastPromptedIndexRef = useRef(-1);
  const onboardingPlannedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const isAdvancingRef = useRef(false);
  const serviceRequestAdvancingRef = useRef(false);
  const serviceRequestSubmittingRef = useRef(false);

  // Base onboarding steps
  const baseSteps = useMemo(() => ([
    { key: "firstName", prompt: "Please enter your first name", type: "text" },
    { key: "lastName", prompt: "Please enter your last name", type: "text" },
    { key: "mobileNumber", prompt: "Please enter your mobile number", type: "text" },
    { key: "loanType", prompt: "Please select Loan Type", type: "select", options: loanTypeOptions },
    { key: "loanAmount", prompt: "Please enter your loan amount", type: "text" },
    { key: "pincode", prompt: "Please enter your pincode", type: "text" }
  ]), []);

  const onboardingSteps = useMemo(() => baseSteps, [baseSteps]);
  const currentOnboardingStep = onboardingActive ? onboardingSteps[onboardingStepIndex] : null;

  // Service Request Steps
  const getServiceRequestSteps = (srType = null) => [
    { key: "srType", prompt: "Please select Service Request Type", type: "select", options: Object.keys(serviceRequestTypes) },
    { key: "srSubtype", prompt: "Please select Service Request Subtype", type: "select", options: srType ? (serviceRequestTypes[srType] || []) : [] },
    { key: "description", prompt: "Please enter Description", type: "text" },
    { key: "attachmentName", prompt: "Please enter Attachment Name", type: "text" },
    { key: "mobileNumber", prompt: "Please enter your 10-digit mobile number", type: "text" }
  ];

  const serviceRequestSteps = useMemo(() => getServiceRequestSteps(serviceRequestData.srType), [serviceRequestData.srType]);
  const currentServiceRequestStep = serviceRequestActive ? serviceRequestSteps[serviceRequestStepIndex] : null;

  // EMI Calculation
  const emiValue = useMemo(() => {
    const principal = loanAmount;
    const monthlyRate = interestRate / (12 * 100);
    const totalMonths = tenureYears * 12;

    if (!principal || !totalMonths) return 0;
    if (monthlyRate === 0) {
      return principal / totalMonths;
    }

    const factor = Math.pow(1 + monthlyRate, totalMonths);
    if (factor === 1) return 0;

    return (principal * monthlyRate * factor) / (factor - 1);
  }, [loanAmount, interestRate, tenureYears]);

  const totalInterest = useMemo(() => {
    const totalPaid = emiValue * tenureYears * 12;
    return Math.max(totalPaid - loanAmount, 0);
  }, [emiValue, tenureYears, loanAmount]);

  // Process bot response and add to messages
  const handleChatResponse = (data) => {
    let messageText = data?.text ?? (typeof data === "string" ? data : "");
    if (typeof messageText === "string") {
      messageText = messageText.replace(/\bnaol\b/gi, "NAOL");
    }
    const isWelcome = typeof messageText === "string" && (messageText.includes("Welcome to Home First Finance") || messageText.includes("NAOL"));
    if ((onboardingActive || onboardingPlannedRef.current || loanDetailsActive || serviceRequestActive || existingCustomerActive || contactUsActive || branchSearchActive) && isWelcome) {
      return;
    }
    const isFirstWelcomeMessage = typeof messageText === "string" && messageText.includes("Welcome to Home First Finance! My name is NAOL");
    const newMessage = {
      id: Date.now(),
      text: messageText,
      isUser: data?.isUser || false,
      timestamp: data?.timestamp ? new Date(data.timestamp) : new Date(),
      showMenuButton: isFirstWelcomeMessage ? false : true
    };
    setMessages((prev) => [...prev, newMessage]);
    setIsTyping(false);
  };

  const sendChatMessage = async (text) => {
    if (!text?.trim()) return false;
    setIsTyping(true);
    try {
      const res = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        body: JSON.stringify({ text: text.trim() })
      });
      const data = await res.json().catch(() => ({}));
      handleChatResponse(data);
      return true;
    } catch (err) {
      console.error("Chat API error:", err);
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: "⚠️ Sorry, I'm having trouble connecting. Please try again.", isUser: false, timestamp: new Date() }
      ]);
      setIsTyping(false);
      return false;
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (!isChatActive || onboardingActive) {
      setIsEmiCalculatorOpen(false);
    }
  }, [isChatActive, onboardingActive]);

  // Start chat handler
  const startChat = (option = null) => {
    setIsChatActive(true);
    setLoanDetailsActive(false);
    setChatDisabled(false);
    setOnboardingError(null);
    setServiceRequestActive(false);
    setServiceRequestError(null);
      setShowContactUs(false);
      setContactUsActive(false);
      setBranchSearchActive(false);
    setExistingCustomerActive(false);
    setInputText("");

    if (option === "New Loan Inquiry") {
      onboardingPlannedRef.current = true;
      setOnboardingActive(true);
      setOnboardingData({});
      setOnboardingStepIndex(0);
      lastPromptedIndexRef.current = 0;
      setMessages(prev => ([
        ...prev,
        { id: "onboarding-start", text: "📋Looking to buy a new home or transfer your existing loan? You’re just a few steps away from personalized loan options", isUser: false, timestamp: new Date() },
        { id: `onboarding-q-0`, text: baseSteps[0].prompt, isUser: false, timestamp: new Date() }
      ]));
      return;
    }

    if (option === "Calculators") {
      setShowCalculatorsPage(true);
      setIsChatActive(false);
      return;
    }

    if (option === "Existing Customer") {
      setIsChatActive(true);
      setExistingCustomerActive(true);
      setMessages([
        {
          id: "existing-customer-1",
          text: "Already a HomeFirst customer? Great! You can easily manage your loan and get updates using our app or web portal.",
          isUser: false,
          timestamp: new Date()
        },
        {
          id: "existing-customer-2",
          text: "You can manage your home loan anytime, 24×7 using the HomeFirst Customer App or Web Portal. From raising service requests and making payments to viewing loan details, statements, and tracking updates everything is available in one place for your convenience.",
          isUser: false,
          timestamp: new Date(),
          showIcons: true,
          showMenuButton: true,
          iconLinks: {
            android: "https://play.google.com/store/apps/details?id=com.iexceed.hffc&hl=en_IN",
            ios: "https://apps.apple.com/in/app/homefirst-customer-portal/id1444845347",
            website: "https://customers.homefirstindia.com/"
          }
        }
      ]);
      return;
    }

    if (option === "FAQ") {
      setIsFAQOpen(true);
      setMessages(prev => ([
        ...prev,
        // { id: "faq-start", text: "Click on a question to view the answer, or ask our chatbot anything", isUser: false, timestamp: new Date() }
      ]));
      return;
    }

    if (option === "Contact Us") {
      setShowCalculatorsPage(false);
      setShowExistingCustomer(false);
      setContactUsActive(true);
      setShowContactUs(false);
      setBranchSearchActive(true);
      const contactIntro =
        "Need help or want to speak to us? Our support team is happy to assist you.\n\n" +
        "Working Hours: Monday to Saturday | 8:00 AM – 6:00 PM";
      const contactDetails =
        "New loan enquiry\n" +
        "Email: newloan@homefirstindia.com\n" +
        "Phone: +918880649911\n\n" +
        "Existing customer\n" +
        "Email: loanfirst@homefirstindia.com\n" +
        "Phone: +918880549911";
      const branchSearchPrompt =
        "📍 Find Nearest Branch\n\n" +
        "Please enter your state, city name, or pincode to find nearby branches.";
      setMessages([
        {
          id: "contact-1",
          text: contactIntro,
          isUser: false,
          timestamp: new Date()
        },
        {
          id: "contact-2",
          text: contactDetails,
          isUser: false,
          timestamp: new Date()
        },
        {
          id: "contact-3",
          text: branchSearchPrompt,
          isUser: false,
          timestamp: new Date()
        }
      ]);
      return;
    }

    if (option) {
      sendChatMessage(option);
    }
  };

  // Send message handler
  const handleSendMessage = () => {
    if (onboardingActive) {
      // Handle free-text answer for current onboarding step
      if (!currentOnboardingStep) return;
      const value = inputText.trim();
      if (value === "") return;

      // Validate current step input
      const { ok, error, value: cleanValue } = validateStepInput(currentOnboardingStep.key, value);
      if (!ok) {
        setMessages(prev => [...prev, { id: Date.now(), text: `⚠️ ${error}`, isUser: false, timestamp: new Date() }]);
        setInputText("");
        return;
      }

      // Add user message
      const userMessage = { id: Date.now(), text: cleanValue ?? value, isUser: true, timestamp: new Date() };
      setMessages(prev => [...prev, userMessage]);
      setInputText("");

      // Save answer and advance with updated data
      setOnboardingData(prev => {
        const updated = { ...prev, [currentOnboardingStep.key]: cleanValue ?? value };
        advanceOnboarding(updated);
        return updated;
      });
      return;
    }

    if (branchSearchActive) {
      // Handle branch search
      const value = inputText.trim();
      if (value === "") return;

      // Add user message
      const userMessage = { id: Date.now(), text: value, isUser: true, timestamp: new Date() };
      setMessages(prev => [...prev, userMessage]);
      setInputText("");
      setIsTyping(true);

      // Search branches (REST API - no socket)
      fetch(`${BRANCH_SEARCH_BASE_URL}/search-branches?query=${encodeURIComponent(value)}`, {
        headers: { accept: "application/json" }
      })
        .then(res => res.json())
        .then(data => {
          setIsTyping(false);
          const results = data.results || [];
          
          if (results.length === 0) {
            setMessages(prev => [...prev, {
              id: Date.now(),
              text: "No branches found for your search. Please try entering a different state, city, or pincode.",
              isUser: false,
              timestamp: new Date()
            }]);
          } else {
            let resultText = `📍 Found ${results.length} nearby branch${results.length > 1 ? "es" : ""}:\n\n`;
            results.forEach((branch, index) => {
              resultText += `${index + 1}. **${branch.location}**\n`;
              resultText += `${branch.address}\n`;
              if (branch.city || branch.state || branch.pincode) {
                const locationParts = [branch.city, branch.state, branch.pincode].filter(Boolean);
                if (locationParts.length > 0) {
                  resultText += `${locationParts.join(", ")}\n`;
                }
              }
              if (branch.phone) {
                resultText += `📞 Phone: ${branch.phone}\n`;
              }
              if (branch.whatsapp) {
                resultText += `💬 WhatsApp: ${branch.whatsapp}\n`;
              }
              resultText += "\n";
            });
            
            setMessages(prev => [...prev, {
              id: Date.now(),
              text: resultText,
              isUser: false,
              timestamp: new Date(),
              showMenuButton: true
            }]);
          }
        })
        .catch(error => {
          console.error("Error searching branches:", error);
          setIsTyping(false);
          setMessages(prev => [...prev, {
            id: Date.now(),
            text: "⚠️ Sorry, I'm having trouble searching for branches. Please try again later.",
            isUser: false,
            timestamp: new Date()
          }]);
        });
      return;
    }

    if (serviceRequestActive) {
      // Handle service request flow
      if (!currentServiceRequestStep) return;
      const value = inputText.trim();
      if (value === "") return;

      // Validate current step input
      let validation = { ok: true, value: value };
      if (currentServiceRequestStep.key === "mobileNumber") {
        validation = validateStepInput("mobileNumber", value);
      } else if (currentServiceRequestStep.key === "description" || currentServiceRequestStep.key === "attachmentName") {
        if (value.length < 3) {
          validation = { ok: false, error: "Please enter at least 3 characters." };
        }
      }

      if (!validation.ok) {
        setMessages(prev => [...prev, { id: Date.now(), text: `⚠️ ${validation.error}`, isUser: false, timestamp: new Date() }]);
        setInputText("");
        return;
      }

      // Add user message
      const userMessage = { id: Date.now(), text: validation.value ?? value, isUser: true, timestamp: new Date() };
      setMessages(prev => [...prev, userMessage]);
      setInputText("");

      // Save answer and advance with updated data
      setServiceRequestData(prev => {
        const updated = { ...prev, [currentServiceRequestStep.key]: validation.value ?? value };
        advanceServiceRequest(updated);
        return updated;
      });
      return;
    }

    if (inputText.trim() === "") return;

    const text = inputText;
    setInputText("");

    const userMessage = {
      id: Date.now(),
      text,
      isUser: true,
      timestamp: new Date()
    };
    setMessages((prev) => [...prev, userMessage]);

    sendChatMessage(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Submit onboarding
  const submitOnboarding = async (payload) => {
    // Prevent duplicate submissions
    if (isSubmittingRef.current) {
      console.log("Submission already in progress, skipping duplicate call");
      return;
    }
    
    setOnboardingError(null);
    setOnboardingSubmitting(true);
    isSubmittingRef.current = true;
    
    console.log("Submitting lead to backend:", payload);
    
    // Validate required fields
    const validation = validateLeadPayload(payload, loanTypeOptions);
    if (!validation.ok) {
      const errorMsg = validation.error || "Missing required information. Please complete all fields.";
      setOnboardingError(errorMsg);
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.text.includes("⚠️") && lastMsg.text === `⚠️ ${errorMsg}`) {
          return prev;
        }
        return [...prev, { id: Date.now(), text: `⚠️ ${errorMsg}`, isUser: false, timestamp: new Date() }];
      });
      setOnboardingSubmitting(false);
      isSubmittingRef.current = false;
      return;
    }
    const cleanPayload = validation.payload;
    
    // Show typing animation while API call is in progress
    setIsTyping(true);
    
    try {
      // Call backend Lead.create
      const res = await fetch(LEAD_CREATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json"
        },
        body: JSON.stringify(cleanPayload)
      });

      const data = await res.json().catch(() => ({}));
      const parsedRawResponse = (() => {
        try {
          if (typeof data?.rawResponse === "string") {
            return JSON.parse(data.rawResponse);
          }
          if (data?.rawResponse && typeof data.rawResponse === "object") {
            return data.rawResponse;
          }
        } catch {
          return null;
        }
        return null;
      })();
      const errorStrings = [
        data?.message,
        data?.detail,
        parsedRawResponse?.message,
        parsedRawResponse?.error
      ]
        .filter(str => typeof str === "string" && str.length > 0)
        .map(str => str.toLowerCase());
      const isDuplicateLead = errorStrings.some(str => str.includes("lead already exist") || str.includes("duplicate_record"));

      if (!res.ok) {
        if (isDuplicateLead) {
          setOnboardingError(null);
          setMessages(prev => ([
            ...prev,
            {
              id: Date.now(),
              text: DUPLICATE_LEAD_MESSAGE,
              isUser: false,
              timestamp: new Date(),
              showMenuButton: true
            }
          ]));
          setOnboardingActive(false);
          onboardingPlannedRef.current = false;
          setOnboardingData({});
          setOnboardingStepIndex(0);
          lastPromptedIndexRef.current = -1;
          return;
        }
        const rawError = data?.message || data?.detail || "Failed to submit your inquiry.";
        const errorMsg = typeof rawError === "string" ? rawError : JSON.stringify(rawError);
        setOnboardingError(errorMsg);
        setMessages(prev => ([
          ...prev,
          { id: Date.now(), text: `⚠️ ${errorMsg}`, isUser: false, timestamp: new Date() }
        ]));
      } else {
        // Show success message with menu button
        const successMessageId = Date.now();
        setMessages(prev => ([
          ...prev,
          { 
            id: successMessageId, 
            text: "📋 Thank you! We've received your loan inquiry. Our team will reach out to you soon.", 
            isUser: false, 
            timestamp: new Date(),
            showMenuButton: true
          }
        ]));
        // Reset onboarding state
        setOnboardingActive(false);
        onboardingPlannedRef.current = false;
        setOnboardingData({});
        setOnboardingStepIndex(0);
        lastPromptedIndexRef.current = -1;
      }
      
    } catch (error) {
      console.error("Error submitting lead:", error);
      const errorMsg = error?.message || "Failed to save your inquiry. Please try again.";
      setOnboardingError(errorMsg);
      setMessages(prev => ([
        ...prev,
        { id: Date.now(), text: `⚠️ ${errorMsg}`, isUser: false, timestamp: new Date() }
      ]));
    } finally {
      setIsTyping(false);
      setOnboardingSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Advance onboarding
  const advanceOnboarding = (updatedData = null) => {
    // Prevent multiple simultaneous calls
    if (isAdvancingRef.current) {
      console.log("Already advancing, skipping duplicate call");
      return;
    }
    
    isAdvancingRef.current = true;
    setTimeout(() => {
      setOnboardingStepIndex(prevIdx => {
        const nextIdx = prevIdx + 1;
        const currentData = updatedData || onboardingData;
        
        if (nextIdx >= onboardingSteps.length) {
          // Completed; submit
          console.log("Onboarding complete, submitting...", currentData);
          setTimeout(() => {
            isAdvancingRef.current = false;
          }, 100);
          if (!onboardingSubmitting && !isSubmittingRef.current) {
            submitOnboarding(currentData);
          } else {
            console.log("Already submitting, skipping duplicate submission");
          }
          return prevIdx; // freeze index
        }
        
        // Ask next question
        if (lastPromptedIndexRef.current !== nextIdx) {
          const nextStep = onboardingSteps[nextIdx];
          if (nextStep) {
            lastPromptedIndexRef.current = nextIdx;
            setMessages(prev => {
              const existingMessage = prev.find(m => m.id === `onboarding-q-${nextIdx}`);
              if (existingMessage) {
                console.log(`Message for step ${nextIdx} already exists, skipping`);
                return prev;
              }
              const messageObj = { 
                id: `onboarding-q-${nextIdx}`, 
                text: nextStep.prompt, 
                isUser: false, 
                timestamp: new Date() 
              };
              // Add options if it's a select type
              if (nextStep.type === "select" && nextStep.options && nextStep.options.length > 0) {
                messageObj.options = nextStep.options;
                messageObj.onOptionClick = (value) => {
                  // Add user message
                  const userMessage = { id: Date.now(), text: value, isUser: true, timestamp: new Date() };
                  setMessages(prevMsgs => [...prevMsgs, userMessage]);
                  // Save answer and advance with updated data
                  setOnboardingData(prevData => {
                    const updated = { ...prevData, [nextStep.key]: value };
                    advanceOnboarding(updated);
                    return updated;
                  });
                };
                messageObj.disabled = onboardingSubmitting;
              }
              return [...prev, messageObj];
            });
          }
        }
        
        setTimeout(() => {
          isAdvancingRef.current = false;
        }, 100);
        return nextIdx;
      });
    }, 250);
  };

  const handleQuickReply = (value) => {
    if (!currentOnboardingStep) return;
    // Add user message
    const userMessage = { id: Date.now(), text: value, isUser: true, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    // Save answer and advance with updated data
    setOnboardingData(prev => {
      const updated = { ...prev, [currentOnboardingStep.key]: value };
      advanceOnboarding(updated);
      return updated;
    });
  };

  const cancelOnboarding = () => {
    setOnboardingActive(false);
    onboardingPlannedRef.current = false;
    setOnboardingData({});
    setOnboardingStepIndex(0);
    setMessages(prev => ([...prev, { id: Date.now(), text: "Onboarding cancelled.", isUser: false, timestamp: new Date() }]));
  };

  // Service Request handlers
  const advanceServiceRequest = (updatedData = null) => {
    // Prevent multiple simultaneous calls
    if (serviceRequestAdvancingRef.current) {
      console.log("Already advancing service request, skipping duplicate call");
      return;
    }
    
    serviceRequestAdvancingRef.current = true;
    setTimeout(() => {
      setServiceRequestStepIndex(prevIdx => {
        const nextIdx = prevIdx + 1;
        const currentData = updatedData || serviceRequestData;
        
        if (nextIdx >= serviceRequestSteps.length) {
          // Completed; submit
          console.log("Service request complete, submitting...", currentData);
          setTimeout(() => {
            serviceRequestAdvancingRef.current = false;
          }, 100);
          if (!serviceRequestSubmitting && !serviceRequestSubmittingRef.current) {
            submitServiceRequest(currentData);
          } else {
            console.log("Already submitting service request, skipping duplicate submission");
          }
          return prevIdx; // freeze index
        }
        
        // Ask next question
        const nextStep = serviceRequestSteps[nextIdx];
        if (nextStep) {
          // If it's the subtype step, update options based on selected type
          let optionsToShow = nextStep.options || [];
          if (nextStep.key === "srSubtype" && currentData.srType) {
            optionsToShow = serviceRequestTypes[currentData.srType] || [];
            nextStep.options = optionsToShow;
          }
          
          setMessages(prev => {
            const existingMessage = prev.find(m => m.id === `service-request-q-${nextIdx}`);
            if (existingMessage) {
              return prev;
            }
            const messageObj = { 
              id: `service-request-q-${nextIdx}`, 
              text: nextStep.prompt, 
              isUser: false, 
              timestamp: new Date(),
              stepKey: nextStep.key,
              stepIndex: nextIdx
            };
            // Add options if it's a select type
            if (nextStep.type === "select" && optionsToShow.length > 0) {
              messageObj.options = optionsToShow;
              messageObj.onOptionClick = (value) => {
                const userMessage = { id: Date.now(), text: value, isUser: true, timestamp: new Date() };
                setMessages(prevMsgs => [...prevMsgs, userMessage]);
                setServiceRequestData(prevData => {
                  const updated = { ...prevData, [nextStep.key]: value };
                  advanceServiceRequest(updated);
                  return updated;
                });
              };
              messageObj.disabled = serviceRequestSubmitting;
            }
            return [...prev, messageObj];
          });
        }
        
        setTimeout(() => {
          serviceRequestAdvancingRef.current = false;
        }, 100);
        return nextIdx;
      });
    }, 250);
  };

  const submitServiceRequest = async (payload) => {
    // Prevent duplicate submissions
    if (serviceRequestSubmittingRef.current) {
      console.log("Service request submission already in progress, skipping duplicate call");
      return;
    }
    
    setServiceRequestError(null);
    setServiceRequestSubmitting(true);
    serviceRequestSubmittingRef.current = true;
    
    // Validate required fields
    if (!payload.srType || !payload.srSubtype || !payload.description || !payload.attachmentName || !payload.mobileNumber) {
      const errorMsg = "Missing required information. Please complete all fields.";
      setServiceRequestError(errorMsg);
      setMessages(prev => ([
        ...prev,
        { id: Date.now(), text: `⚠️ ${errorMsg}`, isUser: false, timestamp: new Date() }
      ]));
      setServiceRequestSubmitting(false);
      serviceRequestSubmittingRef.current = false;
      return;
    }

    // Validate mobile number
    const mobileValidation = validateStepInput("mobileNumber", payload.mobileNumber);
    if (!mobileValidation.ok) {
      setServiceRequestError(mobileValidation.error);
      setMessages(prev => ([
        ...prev,
        { id: Date.now(), text: `⚠️ ${mobileValidation.error}`, isUser: false, timestamp: new Date() }
      ]));
      setServiceRequestSubmitting(false);
      serviceRequestSubmittingRef.current = false;
      return;
    }

    try {
      setIsTyping(true);
      
      const requestPayload = {
        serviceRequestParams: {
          mobile_number: mobileValidation.value,
          Master_Case_Reason__c: payload.srType,
          Case_Reason_sub__c: payload.srSubtype,
          Description: payload.description,
          attachmentName: payload.attachmentName,
          attachment: ""
        }
      };

      const res = await fetch(SERVICE_REQUEST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json"
        },
        body: JSON.stringify(requestPayload)
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errorText = data?.detail || data?.message || data?.error || "Failed to create service request. Please try again.";
        setServiceRequestError(errorText);
        setMessages(prev => ([
          ...prev,
          { id: Date.now(), text: `⚠️ ${errorText}`, isUser: false, timestamp: new Date() }
        ]));
      } else {
        // Show success message with menu button
        const caseId = data?.response?.caseId || "N/A";
        setMessages(prev => {
          const existingSuccessMsg = prev.find(m => 
            m.text && m.text.includes("Service request raised successfully") && m.text.includes(caseId)
          );
          if (existingSuccessMsg) {
            console.log("Success message already exists, skipping duplicate");
            return prev;
          }
          return [...prev, { 
            id: Date.now(), 
            text: `✅ Service request raised successfully! Case ID: ${caseId}`, 
            isUser: false, 
            timestamp: new Date(),
            showMenuButton: true
          }];
        });
        // Reset service request state
        setServiceRequestActive(false);
        setServiceRequestData({});
        setServiceRequestStepIndex(0);
      }
    } catch (error) {
      console.error("Error submitting service request:", error);
      const errorMsg = error?.message || "Failed to create service request. Please try again.";
      setServiceRequestError(errorMsg);
      setMessages(prev => ([
        ...prev,
        { id: Date.now(), text: `⚠️ ${errorMsg}`, isUser: false, timestamp: new Date() }
      ]));
    } finally {
      setIsTyping(false);
      setServiceRequestSubmitting(false);
      serviceRequestSubmittingRef.current = false;
    }
  };

  const cancelServiceRequest = () => {
    setServiceRequestActive(false);
    setServiceRequestData({});
    setServiceRequestStepIndex(0);
    setMessages(prev => ([...prev, { id: Date.now(), text: "Service request cancelled.", isUser: false, timestamp: new Date() }]));
  };

  return (
    <div className="h-screen w-full bg-[#EFEDEC] relative overflow-hidden">
      <div className="fixed inset-0" aria-hidden="true">
        <div className="aurora-gradient"></div>
        <div className="aurora-gradient aurora-gradient--alt"></div>
        <div className="aurora-overlay"></div>
      </div>
      
      <div className="w-full h-full bg-[#EFEDEC] flex flex-col font-sans relative z-10">
        {/* Top Bar */}
        {/* <div className="flex justify-center items-center py-1 px-0 border-b border-gray-200 bg-[#ffffff] relative">
          <h1 className="text-base font-semibold text-gray-800">AI Assistant NAOL</h1>
          <div className={`absolute right-4 w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} title={isConnected ? 'Connected' : 'Disconnected'} />
        </div> */}


        {/* Middle Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-24">
          {showCalculatorsPage ? (
            <CalculatorsPage />
          ) : !isChatActive ? (
            <HomePage onStartChat={startChat} />
          ) : (
            <>
              {isFAQOpen && (
                <FAQ onClose={() => setIsFAQOpen(false)} />
              )}
              <MessageList 
                messages={messages} 
                isTyping={isTyping} 
                messagesEndRef={messagesEndRef} 
              />
            </>
          )}
        </div>

        {/* Bottom Input Area */}
        <div className="absolute bottom-0 left-0 w-full p-4 bg-[#EFEDEC] border-t border-gray-200 z-20">
          {!showCalculatorsPage && isChatActive && !onboardingActive && !serviceRequestActive && !existingCustomerActive && (
            <>
              {branchSearchActive ? (
                <InputArea
                  inputText={inputText}
                  setInputText={setInputText}
                  onSendMessage={handleSendMessage}
                  onKeyPress={handleKeyPress}
                  isEmiCalculatorOpen={isEmiCalculatorOpen}
                  setIsEmiCalculatorOpen={setIsEmiCalculatorOpen}
                  loanAmount={loanAmount}
                  setLoanAmount={setLoanAmount}
                  interestRate={interestRate}
                  setInterestRate={setInterestRate}
                  tenureYears={tenureYears}
                  setTenureYears={setTenureYears}
                  emiValue={emiValue}
                  totalInterest={totalInterest}
                  disabled={chatDisabled}
                  placeholder="Enter state, city, or pincode (e.g., Mumbai, Maharashtra, 400059)..."
                  showQuickActions={false}
                />
              ) : (
                <InputArea
                  inputText={inputText}
                  setInputText={setInputText}
                  onSendMessage={handleSendMessage}
                  onKeyPress={handleKeyPress}
                  isEmiCalculatorOpen={isEmiCalculatorOpen}
                  setIsEmiCalculatorOpen={setIsEmiCalculatorOpen}
                  loanAmount={loanAmount}
                  setLoanAmount={setLoanAmount}
                  interestRate={interestRate}
                  setInterestRate={setInterestRate}
                  tenureYears={tenureYears}
                  setTenureYears={setTenureYears}
                  emiValue={emiValue}
                  totalInterest={totalInterest}
                  disabled={chatDisabled}
                  placeholder="Type your message..."
                  showQuickActions={true}
                />
              )}
            </>
          )}
          {isChatActive && onboardingActive && (
            <OnboardingForm
              currentStep={currentOnboardingStep}
              inputText={inputText}
              setInputText={setInputText}
              onSendMessage={handleSendMessage}
              onQuickReply={handleQuickReply}
              onSubmit={submitOnboarding}
              onCancel={cancelOnboarding}
              stepIndex={onboardingStepIndex}
              totalSteps={onboardingSteps.length}
              error={onboardingError}
              isSubmitting={onboardingSubmitting}
            />
          )}
          {isChatActive && serviceRequestActive && currentServiceRequestStep?.type !== "select" && (
            <ServiceRequestForm
              currentStep={currentServiceRequestStep}
              inputText={inputText}
              setInputText={setInputText}
              onSendMessage={handleSendMessage}
              stepIndex={serviceRequestStepIndex}
              totalSteps={serviceRequestSteps.length}
              error={serviceRequestError}
              isSubmitting={serviceRequestSubmitting}
              onCancel={cancelServiceRequest}
            />
          )}
          
   
        </div>
      </div>
      

    </div>
  );
};

export default App;
